import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import Seo from './marketing/Seo'
import fdm from '../Assets/fd-logo.png'
import './GuestUpload.css'

const DEFAULT_MESSAGE =
    "We'd love to see your photos! Please share your favourite moments from the day."

const firstName = (name) => (name ? String(name).trim().split(' ')[0] : '')

const GuestUpload = () => {
    const { weddingId } = useParams()
    const fileInputRef = useRef(null)

    const [statusLoading, setStatusLoading] = useState(true)
    const [status, setStatus] = useState({
        isAvailable: false,
        reason: null,
        maxImages: 5,
        clientName: '',
        spouseName: '',
        hashtag: '',
        couplePhoto: '',
        colourScheme: 'pink',
        guestPhotoMessage: DEFAULT_MESSAGE,
    })

    const [from, setFrom] = useState('')
    const [message, setMessage] = useState('')
    const [files, setFiles] = useState([])
    const [submitLoading, setSubmitLoading] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [submitSuccess, setSubmitSuccess] = useState(false)

    const maxImages = status?.maxImages || 5

    const filesRef = useRef([])
    filesRef.current = files

    useEffect(() => {
        let mounted = true
        setStatusLoading(true)

        axios
            .post(`${process.env.REACT_APP_API}/guest-gallery/status-by-id`, { weddingId })
            .then((res) => {
                if (!mounted) return
                setStatus(res.data || { isAvailable: false, reason: 'serverError', maxImages: 5 })
                setStatusLoading(false)
            })
            .catch(() => {
                if (!mounted) return
                setStatus({ isAvailable: false, reason: 'serverError', maxImages: 5 })
                setStatusLoading(false)
            })

        return () => {
            mounted = false
        }
    }, [weddingId])

    useEffect(() => {
        return () => {
            filesRef.current.forEach((item) => {
                if (item?.preview) URL.revokeObjectURL(item.preview)
            })
        }
    }, [])

    const coupleLine = useMemo(() => {
        const a = firstName(status.clientName)
        const b = firstName(status.spouseName)
        if (a && b) return `${a} & ${b}`
        return a || b || ''
    }, [status.clientName, status.spouseName])

    const availabilityMessage = useMemo(() => {
        if (statusLoading) return null
        if (status?.isAvailable) return null

        const r = status?.reason
        if (r === 'uploadWindowClosed') {
            return coupleLine
                ? `The guest gallery for ${coupleLine} has now closed.`
                : 'The guest gallery has now closed.'
        }
        if (r === 'notWeddingDay') {
            return coupleLine
                ? `Uploads open on ${coupleLine}'s wedding day.`
                : 'Uploads are only open on the wedding day.'
        }
        if (r === 'notPremium' || r === 'ownerNotFound') {
            return 'The guest gallery is not available for this wedding.'
        }
        if (r === 'weddingNotFound') return "We couldn't find this wedding."
        if (r === 'weddingDateMissing') return "Uploads aren't available yet."
        return 'Uploads are not available right now.'
    }, [statusLoading, status, coupleLine])

    const addFiles = (incoming) => {
        setSubmitError('')
        setSubmitSuccess(false)

        const images = Array.from(incoming || []).filter((file) =>
            String(file?.type || '').startsWith('image/')
        )
        if (!images.length) return

        setFiles((prev) => {
            const room = Math.max(0, maxImages - prev.length)
            const next = images.slice(0, room).map((file) => ({
                file,
                preview: URL.createObjectURL(file),
                name: file.name,
            }))
            return [...prev, ...next]
        })
    }

    const onPickFiles = (e) => {
        addFiles(e.target.files)
        e.target.value = ''
    }

    const onDrop = (e) => {
        e.preventDefault()
        if (submitLoading || !status?.isAvailable) return
        addFiles(e.dataTransfer.files)
    }

    const removeFile = (index) => {
        setFiles((prev) => {
            const next = [...prev]
            const [removed] = next.splice(index, 1)
            if (removed?.preview) URL.revokeObjectURL(removed.preview)
            return next
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitError('')
        setSubmitSuccess(false)

        const trimmedFrom = String(from || '').trim()
        const trimmedMessage = String(message ?? '')

        if (!trimmedFrom) {
            setSubmitError('Please add your name.')
            return
        }
        if (files.length < 1) {
            setSubmitError('Please choose at least 1 photo.')
            return
        }
        if (files.length > maxImages) {
            setSubmitError(`You can upload up to ${maxImages} photos.`)
            return
        }

        try {
            setSubmitLoading(true)

            const form = new FormData()
            form.append('weddingId', weddingId)
            form.append('from', trimmedFrom)
            form.append('message', trimmedMessage)
            files.forEach((item) => form.append('images', item.file))

            await axios.post(`${process.env.REACT_APP_API}/guest-gallery/submit-by-id`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })

            files.forEach((file) => {
                if (file?.preview) URL.revokeObjectURL(file.preview)
            })
            setSubmitSuccess(true)
            setFrom('')
            setMessage('')
            setFiles([])
        } catch (err) {
            const msg = err?.response?.data?.error || 'Upload failed. Please try again.'
            setSubmitError(msg)
        } finally {
            setSubmitLoading(false)
        }
    }

    const scheme = status.colourScheme || 'pink'
    const pageTitle = coupleLine ? `${coupleLine} | Guest gallery` : 'Guest gallery | First Dance'

    return (
        <div className={`guest-upload-page scheme-${scheme}`}>
            <Seo
                title={pageTitle}
                description="Share your favourite photos and a message from the wedding day."
                path={`/upload/${weddingId}`}
            />
            <meta name="robots" content="noindex, nofollow" />

            <div className="guest-upload-inner">
                <p className="guest-upload-kicker">Guest gallery</p>
                <h1 className="guest-upload-title">Share your photos</h1>

                {coupleLine ? <p className="guest-upload-names">{coupleLine}</p> : null}
                {status.hashtag ? (
                    <p className="guest-upload-hashtag">#{String(status.hashtag).replace(/^#/, '')}</p>
                ) : null}

                {status.couplePhoto ? (
                    <img
                        src={status.couplePhoto}
                        alt={coupleLine ? coupleLine : 'The couple'}
                        className="guest-upload-photo"
                    />
                ) : null}

                <p className="guest-upload-intro">
                    {status.guestPhotoMessage || DEFAULT_MESSAGE}
                </p>

                <div className="guest-upload-card">
                    {statusLoading ? (
                        <div className="guest-upload-loader" aria-label="Loading" />
                    ) : submitSuccess ? (
                        <div className="guest-upload-success-panel">
                            <h2>Thank you</h2>
                            <p>
                                {coupleLine
                                    ? `Your photos are with ${coupleLine}.`
                                    : 'Your photos have been sent.'}
                            </p>
                            <button
                                type="button"
                                className="guest-upload-submit"
                                onClick={() => setSubmitSuccess(false)}
                            >
                                Send more
                            </button>
                        </div>
                    ) : availabilityMessage ? (
                        <p className="guest-upload-status">{availabilityMessage}</p>
                    ) : (
                        <form className="guest-upload-form" onSubmit={handleSubmit}>
                            <label className="guest-upload-field">
                                <span>From</span>
                                <input
                                    type="text"
                                    value={from}
                                    onChange={(e) => setFrom(e.target.value)}
                                    placeholder="Your name"
                                    maxLength={60}
                                    disabled={submitLoading}
                                    autoComplete="name"
                                    required
                                />
                            </label>

                            <label className="guest-upload-field">
                                <span>Message</span>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Leave a note for the couple"
                                    maxLength={500}
                                    disabled={submitLoading}
                                    rows={4}
                                />
                            </label>

                            <div className="guest-upload-field">
                                <span>Photos (up to {maxImages})</span>
                                <div
                                    className="guest-upload-dropzone"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={onDrop}
                                    onClick={() => !submitLoading && files.length < maxImages && fileInputRef.current?.click()}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            fileInputRef.current?.click()
                                        }
                                    }}
                                >
                                    <input
                                        ref={fileInputRef}
                                        name="images"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={onPickFiles}
                                        disabled={submitLoading || files.length >= maxImages}
                                    />
                                    <p>Tap to add photos, or drop them here</p>
                                    <p className="guest-upload-hint">Up to {maxImages} images, 8MB each</p>
                                </div>

                                {files.length > 0 ? (
                                    <div className="guest-upload-previews">
                                        {files.map((item, index) => (
                                            <div key={`${item.name}-${index}`} className="guest-upload-preview">
                                                <img src={item.preview} alt="" />
                                                <button
                                                    type="button"
                                                    className="guest-upload-remove"
                                                    onClick={() => removeFile(index)}
                                                    aria-label="Remove photo"
                                                    disabled={submitLoading}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>

                            {submitError ? <p className="guest-upload-error">{submitError}</p> : null}

                            <button className="guest-upload-submit" type="submit" disabled={submitLoading}>
                                {submitLoading ? 'Uploading...' : 'Send'}
                            </button>
                        </form>
                    )}
                </div>

                <a className="guest-upload-footer" href="https://firstdance.app" target="_blank" rel="noreferrer">
                    <p>Sent with</p>
                    <img src={fdm} alt="First Dance" />
                </a>
            </div>
        </div>
    )
}

export default GuestUpload
